import { useState, useRef, useEffect } from 'react';
import { FiCalendar, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const DatePicker = ({ 
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange, 
  placeholder = 'Select date range', 
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(startDate ? new Date(startDate) : new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const pickerRef = useRef(null);

  // Determine if we're selecting end date based on what's already selected
  // If we have a start date but no end date, we're selecting the end
  const isSelectingEnd = startDate && !endDate;

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  // Format date to YYYY-MM-DD in local timezone (not UTC)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSelectDate = (date) => {
    if (!date) return;
    
    const formatted = formatDateLocal(date);
    
    if (!startDate || (startDate && endDate)) {
      // No dates selected OR both dates selected -> start fresh with new start date
      onStartDateChange(formatted);
      onEndDateChange('');
      // Keep picker open to select end date
    } else {
      // We have start date but no end date -> set end date
      const start = new Date(startDate + 'T00:00:00'); // Parse as local time
      if (date < start) {
        // If clicked date is before start, swap them
        onStartDateChange(formatted);
        onEndDateChange(startDate);
      } else {
        onEndDateChange(formatted);
      }
      // Close picker after selecting range
      setIsOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onStartDateChange('');
    onEndDateChange('');
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDisplayValue = () => {
    if (!startDate && !endDate) return placeholder;
    if (startDate && !endDate) return `${formatDisplayDate(startDate)} → ...`;
    if (startDate && endDate) return `${formatDisplayDate(startDate)} → ${formatDisplayDate(endDate)}`;
    return placeholder;
  };

  const isInRange = (date) => {
    if (!date || !startDate) return false;
    const start = new Date(startDate);
    const current = date;
    
    // Show hover preview when selecting end date
    if (isSelectingEnd && hoverDate) {
      const hover = hoverDate;
      const rangeStart = start < hover ? start : hover;
      const rangeEnd = start < hover ? hover : start;
      return current > rangeStart && current < rangeEnd;
    }
    
    // Show actual range when both dates selected
    if (endDate) {
      const end = new Date(endDate);
      return current > start && current < end;
    }
    
    return false;
  };

  const isStartDate = (date) => {
    if (!date || !startDate) return false;
    return formatDateLocal(date) === startDate;
  };

  const isEndDate = (date) => {
    if (!date || !endDate) return false;
    return formatDateLocal(date) === endDate;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const days = getDaysInMonth(viewDate);

  return (
    <div ref={pickerRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
      >
        <span className={(startDate || endDate) ? 'text-gray-900' : 'text-gray-500'}>
          {getDisplayValue()}
        </span>
        <div className="flex items-center gap-1">
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-gray-200 rounded transition-colors"
            >
              <FiX className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
          <FiCalendar className="text-gray-400 flex-shrink-0" />
        </div>
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-80">
          {/* Selection hint */}
          <div className={`mb-3 px-2 py-1.5 rounded text-xs text-center ${
            isSelectingEnd 
              ? 'bg-green-50 text-green-700' 
              : 'bg-blue-50 text-blue-700'
          }`}>
            {isSelectingEnd 
              ? '✓ Start selected! Now click to select end date' 
              : '👆 Click to select start date'}
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <FiChevronLeft className="text-gray-600" />
            </button>
            <span className="font-medium text-gray-900">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <FiChevronRight className="text-gray-600" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAYS.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((date, index) => {
              const inRange = isInRange(date);
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelectDate(date)}
                  onMouseEnter={() => date && setHoverDate(date)}
                  onMouseLeave={() => setHoverDate(null)}
                  disabled={!date}
                  className={`
                    aspect-square flex items-center justify-center text-sm transition-colors
                    ${!date ? 'invisible' : ''}
                    ${isStart || isEnd
                      ? 'bg-blue-600 text-white font-medium rounded-full' 
                      : inRange
                        ? 'bg-blue-100 text-blue-700'
                        : isToday(date)
                          ? 'bg-gray-100 text-gray-900 font-medium rounded-full'
                          : 'text-gray-700 hover:bg-gray-100 rounded-full'
                    }
                    ${inRange && !isStart && !isEnd ? 'rounded-none' : ''}
                    ${isStart && endDate ? 'rounded-r-none' : ''}
                    ${isEnd && startDate ? 'rounded-l-none' : ''}
                  `}
                >
                  {date?.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={(e) => {
                handleClear(e);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  const today = formatDateLocal(new Date());
                  onStartDateChange(today);
                  onEndDateChange(today);
                  setIsOpen(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  const today = new Date();
                  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                  onStartDateChange(formatDateLocal(weekAgo));
                  onEndDateChange(formatDateLocal(today));
                  setIsOpen(false);
                }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Last 7 days
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
